import './LoginForm.css'
import {useState} from 'react';
import {useNavigate} from "react-router-dom";
import { Globals } from '../globalfunctions.js';
import simpleSessionTimer from '../utils/simpleSessionTimer';
import mfaEnforcer from '../utils/mfaEnforcer';
import TwoFactorAuth from '../components/TwoFactorAuth';
import ConfirmDialog from '../components/ConfirmDialog';
import ServiceStatusBanner from '../components/ServiceStatusBanner';

function LoginForm() {
  const navigate = useNavigate();

    const [UserName, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showTwoFactor, setShowTwoFactor] = useState(false);
    const [loginData, setLoginData] = useState(null);
    const [loginError, setLoginError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoginError('');
        setIsSubmitting(true);

        var dest = process.env.REACT_APP_BLADE_API_URL+"/api/users/checkuser";

        try {
            const response = await fetch(dest, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              // Without this, the browser won't store the httpOnly refresh
              // cookie CheckUser sets (setRefreshCookie in
              // users.controller.js) since the API is a different origin -
              // silent refresh (authFetch.js) would then always fail the
              // first time the access token expires.
              credentials: 'include',
              body: JSON.stringify({ UserName, password })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Login failed');
            }

            const TempResponse = await response.json();

            // 2FA is now mandatory for all users
            // Check if user has completed 2FA setup
            if (!TempResponse.user.twoFactorSetup) {
                // User needs to set up 2FA (either new user or admin reset)
                setLoginData({
                    token: TempResponse.token,
                    user: {
                        Id: TempResponse.user.id,
                        UserName: TempResponse.user.username,
                        UserType: TempResponse.user.role_type,
                        FirstName: TempResponse.user.first_name,
                        LastName: TempResponse.user.last_name
                    },
                    requiresSetup: true  // Flag to indicate this is setup mode
                });

                // Show 2FA setup instead of verification
                setShowTwoFactor(true);
                localStorage.setItem('accessToken', TempResponse.token);
                return;
            }
            // All users must verify 2FA at login
            setLoginData({
                token: TempResponse.token,
                user: {
                    Id: TempResponse.user.id,
                    UserName: TempResponse.user.username,
                    UserType: TempResponse.user.role_type,
                    FirstName: TempResponse.user.first_name,
                    LastName: TempResponse.user.last_name
                }
            });

            // Show mandatory 2FA verification
            setShowTwoFactor(true);
            localStorage.setItem('accessToken', TempResponse.token);
        } catch (error) {
            console.error('Login failed', error);
            setLoginError(error.message || 'Login failed. Please check your credentials.');
        } finally {
            setIsSubmitting(false);
        }
    }

    const completeLogin = (responseData) => {
        // Populate global state
        Globals.Id = responseData.id;
        Globals.UserName = UserName;
        Globals.UserType = responseData.role_type;
        Globals.FirstName = responseData.first_name;
        Globals.LastName = responseData.last_name;
        Globals.LoginTime = new Date();

        // Store the JWT and user data
        localStorage.setItem('accessToken', responseData.token);

        // Store user data for restoration after refresh
        const userData = {
            Id: responseData.id,
            UserName: UserName,
            UserType: responseData.role_type,
            FirstName: responseData.first_name,
            LastName: responseData.last_name
        };
        localStorage.setItem('userData', JSON.stringify(userData));

        // Session expiry is now handled by authFetch's silent refresh, not
        // a fixed client-side timer - see index.js for why startMonitoring
        // was removed. startSession() bookkeeping kept, harmless.
        simpleSessionTimer.startSession();

        // Check for intended redirect URL
        const redirectUrl = localStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
            localStorage.removeItem('redirectAfterLogin');
            navigate(redirectUrl);
        } else if (responseData.role_type === 'customer') {
            navigate('/portal');
        } else {
            navigate('/home');
        }
    }

    const handle2FASuccess = (method) => {
        if (!loginData?.requiresSetup) {
            // This was a verification process
        }
        // (setup process is handled server-side by TwoFactorAuth itself)

        // Mark 2FA session as verified
        mfaEnforcer.markSessionAs2FAVerified();

        // Mark 2FA setup as completed
        localStorage.setItem('2faCompleted', 'true');

        if (loginData) {
            completeLogin({
                token: loginData.token,
                id: loginData.user.Id,
                role_type: loginData.user.UserType,
                first_name: loginData.user.FirstName,
                last_name: loginData.user.LastName
            });
        }
        setShowTwoFactor(false);
        setLoginData(null);
    }

    const handle2FACancelRequest = () => {
        setShowCancelConfirm(true);
    }

    const confirmCancel2FA = () => {
        const isSetup = loginData?.requiresSetup;
        const errorMessage = isSetup
            ? 'Login cancelled. Two-factor authentication setup is required.'
            : 'Login cancelled. Two-factor authentication is required.';

        setShowTwoFactor(false);
        setLoginData(null);
        localStorage.removeItem('accessToken');
        setLoginError(errorMessage);
        setShowCancelConfirm(false);
    }


  return (
    <div style={{ display: 'flex', height: '100vh', width: '100vw', content: 'center', alignItems: 'center', justifyContent: 'center'}} className="NotTheBody">
    <ServiceStatusBanner />
    <div className='wrapper'>
        <form onSubmit={handleSubmit}>
            <h1>Login</h1>
            <div className="input-box">
                <input
                    id="username"
                    name="username"
                    autoComplete="username"
                    autoFocus
                    placeholder="Username OR Email"
                    value={UserName}
                    onChange={(e) => { setUserName(e.target.value) }}
                    required
                />
            </div>
            <div className="input-box">
                <input
                    id="password"
                    name="current-password"
                    autoComplete="current-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value) }}
                    required
                />
                <span
                    className="icon"
                    role="button"
                    tabIndex={0}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword(!showPassword)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowPassword(!showPassword); }}
                    style={{ cursor: 'pointer' }}
                >
                    {showPassword ? '🙈' : '👁️'}
                </span>
            </div>
            {loginError && (
                <div style={{
                    color: 'red',
                    backgroundColor: '#ffe6e6',
                    padding: '10px',
                    borderRadius: '4px',
                    marginBottom: '15px',
                    textAlign: 'center'
                }}>
                    {loginError}
                </div>
            )}
            <button type='submit' disabled={isSubmitting}>{isSubmitting ? 'Logging in...' : 'Login'}</button>
            <div className="register-link">
                <p>Don't have an account? <a href="register">Register</a></p>
            </div>

            <div className="register-link">
                <p>Forgot your password? <a href="ForgotPassword">Reset Password</a></p>
            </div>
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#7c8993', borderRadius: '4px', fontSize: '12px' }}>
                🔒 <strong>Security Notice:</strong> Two-factor authentication is required for all accounts. New users and users with reset 2FA will be guided through the setup process.
            </div>
        </form>
    </div>
    {showTwoFactor && (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        }}>
            <div style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '8px',
                maxWidth: '500px',
                width: '90%',
                textAlign: 'center'
            }}>
                <h2 style={{ marginBottom: '20px', color: '#007bff' }}>
                    {loginData?.requiresSetup ? '🔧 Setup Two-Factor Authentication' : '🔒 Two-Factor Authentication Required'}
                </h2>
                <p style={{ marginBottom: '20px', color: '#666' }}>
                    {loginData?.requiresSetup
                        ? 'Your account requires two-factor authentication setup. Please configure your preferred 2FA method to continue.'
                        : 'Two-factor authentication is mandatory for all users. Please verify your identity to continue.'
                    }
                </p>
                <TwoFactorAuth
                    mode={loginData?.requiresSetup ? "setup" : "login"}
                    onSuccess={handle2FASuccess}
                    onCancel={handle2FACancelRequest}
                />
            </div>
        </div>
    )}
    <ConfirmDialog
        open={showCancelConfirm}
        title={loginData?.requiresSetup ? 'Cancel 2FA Setup?' : 'Cancel Login?'}
        message={loginData?.requiresSetup
            ? 'Two-factor authentication setup is required to access your account. Are you sure you want to cancel?'
            : 'Two-factor authentication is required to login. Are you sure you want to cancel?'}
        confirmLabel="Yes, Cancel"
        cancelLabel="Go Back"
        onConfirm={confirmCancel2FA}
        onCancel={() => setShowCancelConfirm(false)}
    />
    </div>
  )
}

export default LoginForm
